import React, { FC, useCallback, useState } from "react";
import { ButtonProps, Dialog, Input, URLInput } from "@xapp/components";

import { BasicIntegrationCard, IntegrationCardProp, IntegrationConnectionProps } from "./BasicIntegrationCard";

export interface Free2GrowConnectionProps extends IntegrationConnectionProps {
    integration: "free2grow";
    apiKey: string;
    endpoint?: string;
    service?: string;
}

export interface Free2GrowIntegrationCardProps extends IntegrationCardProp {
    apiKey?: string;
    endpoint?: string;
    service?: string;

    onConnect?(props: Free2GrowConnectionProps): void;
}

/**
 * Free-2-Grow (Archer) setup.
 *
 * Deliberately one required field. The partner key is shared across every business and one
 * endpoint serves them all, so a normal connection is just the key -- the endpoint and service
 * overrides are here for a test account and should stay empty otherwise. Sending an override that
 * matches the default would pin a copy of it to this business.
 */
export const Free2GrowIntegrationCard: FC<Free2GrowIntegrationCardProps> = (props) => {

    const { checked, disabled, isLinked, isActive, onConnect, onDisconnect } = props;

    const [apiKey, setApiKey] = useState<string>(props.apiKey || "");
    const [endpoint, setEndpoint] = useState<string>(props.endpoint || "");
    const [service, setService] = useState<string>(props.service || "");
    const [showDialog, setShowDialog] = useState<boolean>(false);

    const resetForm = useCallback(() => {
        setApiKey(props.apiKey || "");
        setEndpoint(props.endpoint || "");
        setService(props.service || "");
    }, [props.apiKey, props.endpoint, props.service]);

    const handleCloseDialog = useCallback(() => {
        resetForm();
        setShowDialog(false);
    }, [resetForm]);

    const handleFree2GrowClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>): void => {
        e.preventDefault();

        if (typeof checked === "boolean") {
            if (typeof props.onClick === "function") {
                props.onClick(e);
            }
        } else {
            setShowDialog(true);
        }
    }, [checked, props.onClick]);

    const handleConnectFree2Grow = useCallback(() => {
        if (typeof onConnect === "function") {
            onConnect({
                integration: "free2grow",
                apiKey,
                endpoint: endpoint || undefined,
                service: service || undefined
            });
            setShowDialog(false);
        }
    }, [onConnect, apiKey, endpoint, service]);

    const isValidEndpoint = useCallback((url: string): boolean => {
        if (!url) {
            // Empty is valid -- it means "use the production endpoint".
            return true;
        }
        try {
            new URL(url);
            return true;
        } catch {
            return false;
        }
    }, []);

    const actions: ButtonProps[] = [
        {
            label: "Close",
            variation: "secondary",
            onClick: handleCloseDialog
        }
    ];

    if (typeof onConnect === "function") {
        actions.push({
            label: "Connect",
            variation: "primary",
            disabled: !apiKey || !isValidEndpoint(endpoint),
            onClick: handleConnectFree2Grow
        });
    }

    return (
        <>
            <BasicIntegrationCard
                checked={checked}
                disabled={disabled}
                imageUrl="/img/integrations/free-2-grow/free-2-grow-logo.png"
                text="Free-2-Grow"
                href={() => "/integrations/free2grow"}
                onClick={handleFree2GrowClick}
                isLinked={isLinked}
                isActive={isActive}
                onDisconnect={onDisconnect}
            />
            <Dialog
                active={showDialog}
                actions={actions}
                title="Free-2-Grow Setup"
                onClose={handleCloseDialog}
            >
                <p>Leads are sent to Free-2-Grow's Archer platform for call and text outreach.</p>
                <p>
                    Archer must enrol this business before leads will be actioned -- until then they
                    are accepted and held.
                </p>
                <Input label="API Key" type="password" value={apiKey} onChange={(val) => setApiKey(String(val))} />
                <URLInput label="Endpoint URL (Optional)" value={endpoint} onChange={(val) => setEndpoint(String(val))} />
                <Input label="Service (Optional)" value={service} onChange={(val) => setService(String(val))} />
            </Dialog>
        </>
    );
}
